import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, ArrowLeft, RefreshCw } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { SignaturePad, type SignaturePadValue } from '@/components/SignaturePad';
import { useTechAuth } from '@/contexts/TechAuthContext';
import {
  getTechDiagnosticSession,
  TechDiagnosticSession,
  updateTechDiagnosticSession,
} from '@/lib/tech-api-client';

type PresentationOptionId = 'option_1' | 'option_2' | 'option_3' | 'option_4';

type PresentationDecision = PresentationOptionId | 'declined' | '';

type OptionCard = {
  id: PresentationOptionId;
  title: string;
  totalDueTodayUsd: number | null;
  membershipFirstPaymentUsd: number | null;
  notes: string;
};

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error || 'Unknown error');
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function formatUsd(value: number | null): string {
  if (value === null) return '—';
  return `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function normalizeDecision(raw: unknown): PresentationDecision {
  const normalized = String(raw || '').trim().toLowerCase();
  if (normalized === 'declined') return 'declined';
  if (normalized === 'good' || normalized === 'option_1' || normalized === 'option1') return 'option_1';
  if (normalized === 'better' || normalized === 'option_2' || normalized === 'option2') return 'option_2';
  if (normalized === 'best' || normalized === 'option_3' || normalized === 'option3') return 'option_3';
  if (normalized === 'replacement' || normalized === 'option_4' || normalized === 'option4') return 'option_4';
  return '';
}

function toLines(text: string): string[] {
  return String(text || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

export default function TechDiagnosticPresentationPage() {
  const { id } = useParams();
  const sessionId = String(id || '').trim();
  const { accessToken } = useTechAuth();
  const navigate = useNavigate();

  const [session, setSession] = useState<TechDiagnosticSession | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [coachPanelOpen, setCoachPanelOpen] = useState(false);

  const [decision, setDecision] = useState<PresentationDecision>('');
  const [confirmingOption, setConfirmingOption] = useState<PresentationOptionId | null>(null);

  const confirmRef = useRef<HTMLDivElement | null>(null);

  const [authorizationToProceed, setAuthorizationToProceed] = useState(false);
  const [signedName, setSignedName] = useState('');
  const [signatureDraft, setSignatureDraft] = useState<SignaturePadValue>({ dataUrl: '', isEmpty: true });
  const [savedSignatureDataUrl, setSavedSignatureDataUrl] = useState('');
  const [savedSignedAt, setSavedSignedAt] = useState<string | null>(null);

  const load = async () => {
    if (!accessToken) return;
    if (!sessionId) return;

    setLoading(true);
    setError(null);
    try {
      const s = await getTechDiagnosticSession(accessToken, sessionId);
      setSession(s);

      setDecision(normalizeDecision(s.option_selected));

      const sig = (s.customer_signature || {}) as Record<string, unknown>;
      const sigDataUrl =
        typeof sig.signature_data_url === 'string'
          ? sig.signature_data_url
          : typeof sig.data_url === 'string'
            ? sig.data_url
            : '';

      setSavedSignatureDataUrl(sigDataUrl);
      setSavedSignedAt(s.customer_signed_at ? String(s.customer_signed_at) : null);
      setSignedName(typeof sig.signed_name === 'string' ? sig.signed_name : '');
      setAuthorizationToProceed(Boolean(sig.authorization_to_proceed));
      setSignatureDraft({ dataUrl: '', isEmpty: true });
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, sessionId]);

  useEffect(() => {
    if (!confirmingOption) return;
    confirmRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [confirmingOption]);

  const optionCards = useMemo<OptionCard[]>(() => {
    if (!session) return [];

    const optionsRaw = (session.options_presented || {}) as Record<string, unknown>;

    const build = (id: PresentationOptionId): OptionCard | null => {
      const fallbackKey = id === 'option_1' ? 'good' : id === 'option_2' ? 'better' : id === 'option_3' ? 'best' : 'replacement';
      const raw = (optionsRaw[id] || optionsRaw[id.replace('_', '')] || optionsRaw[fallbackKey] || {}) as Record<string, unknown>;

      const title = String(raw.title || '').trim();
      const totalDueTodayUsd = toNumber(raw.total_due_today_usd ?? raw.total_usd ?? raw.price_usd);
      const membershipFirstPaymentUsd = toNumber(raw.membership_first_payment_usd);
      const notes = String(raw.notes || '').trim();

      if (!title && totalDueTodayUsd === null && membershipFirstPaymentUsd === null && !notes) return null;

      return {
        id,
        title: title || id.replace('_', ' ').toUpperCase(),
        totalDueTodayUsd,
        membershipFirstPaymentUsd,
        notes,
      };
    };

    return (['option_1', 'option_2', 'option_3', 'option_4'] as const)
      .map((idVal) => build(idVal))
      .filter(Boolean) as OptionCard[];
  }, [session]);

  const decline = async () => {
    if (!accessToken) return;
    if (!sessionId) return;

    setSaving(true);
    setError(null);
    try {
      const updated = await updateTechDiagnosticSession(accessToken, sessionId, {
        option_selected: 'declined',
        customer_signed_at: null,
        customer_signature: {},
      });

      setSession(updated);
      setDecision('declined');
      setConfirmingOption(null);
      setSavedSignatureDataUrl('');
      setSavedSignedAt(null);
      setAuthorizationToProceed(false);
      setSignatureDraft({ dataUrl: '', isEmpty: true });
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmAccept = async () => {
    if (!accessToken) return;
    if (!sessionId) return;
    if (!confirmingOption) return;

    if (!authorizationToProceed) {
      setError('Authorization to proceed is required.');
      return;
    }

    if (!signedName.trim()) {
      setError('Please type the customer name.');
      return;
    }

    if (signatureDraft.isEmpty && !savedSignatureDataUrl) {
      setError('Customer signature is required.');
      return;
    }

    const signatureDataUrl = signatureDraft.isEmpty ? savedSignatureDataUrl : signatureDraft.dataUrl;
    const signedAt = new Date().toISOString();

    setSaving(true);
    setError(null);
    try {
      const updated = await updateTechDiagnosticSession(accessToken, sessionId, {
        option_selected: confirmingOption,
        customer_signed_at: signedAt,
        customer_signature: {
          signed_name: signedName.trim(),
          signature_data_url: signatureDataUrl,
          authorization_to_proceed: true,
        },
        status: 'completed',
      });

      setSession(updated);
      setDecision(confirmingOption);
      setSavedSignatureDataUrl(signatureDataUrl);
      setSavedSignedAt(signedAt);
      setSignatureDraft({ dataUrl: '', isEmpty: true });
      setConfirmingOption(null);
      navigate(`/diagnostics/${encodeURIComponent(sessionId)}/invoice`);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (!sessionId) {
    return (
      <div className="p-6 bg-white border border-gray-200 rounded-2xl">
        <div className="text-sm text-red-800 font-medium">Missing diagnostic session id.</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(`/diagnostics/${encodeURIComponent(sessionId)}`)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Presentation Mode</h1>
          </div>
          <p className="text-gray-600 mt-2">
            Session: <span className="font-mono text-xs">{sessionId}</span>
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setCoachPanelOpen((v) => !v)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50"
          >
            {coachPanelOpen ? 'Hide coach panel' : 'Show coach panel'}
          </button>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50"
            disabled={loading}
          >
            <RefreshCw className={['w-4 h-4', loading ? 'animate-spin' : ''].join(' ')} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800 font-medium">{error}</p>
          </div>
        </div>
      )}

      {coachPanelOpen ? (
        <div className="bg-gray-900 text-white rounded-2xl p-4 space-y-2">
          <div className="text-sm font-semibold">Coach panel (tech only)</div>
          <div className="text-sm text-gray-200">
            Keep it simple and value-based. Ask one question, then pause.
          </div>
          <ul className="text-sm text-gray-200 list-disc pl-5 space-y-1">
            <li>“Here are 4 ways we can handle this today — I’ll walk you through each.”</li>
            <li>“Option 2 includes Comfort Club savings and covers your maintenance moving forward.”</li>
            <li>“If you’d like, we can start with Option 1 and still plan for upgrades later.”</li>
          </ul>
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {optionCards.map((opt) => {
          const active = decision === opt.id;
          const noteLines = toLines(opt.notes);

          return (
            <div
              key={opt.id}
              className={[
                'rounded-2xl border p-5 space-y-4',
                active ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200',
              ].join(' ')}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-xs text-gray-500">{opt.id.replace('_', ' ').toUpperCase()}</div>
                  <div className="mt-1 text-lg font-semibold text-gray-900">{opt.title}</div>
                </div>
                {active ? (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-medium bg-green-100 text-green-800 border-green-200">
                    Selected
                  </span>
                ) : null}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-xl p-3">
                  <div className="text-xs text-gray-500">Total due today</div>
                  <div className="mt-1 text-xl font-bold text-gray-900">{formatUsd(opt.totalDueTodayUsd)}</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <div className="text-xs text-gray-500">Membership first payment</div>
                  <div className="mt-1 text-xl font-bold text-gray-900">{formatUsd(opt.membershipFirstPaymentUsd)}</div>
                </div>
              </div>

              {noteLines.length ? (
                <div className="bg-gray-50 rounded-xl p-3">
                  <div className="text-xs text-gray-500">What’s included</div>
                  <ul className="mt-2 text-sm text-gray-800 list-disc pl-5 space-y-1">
                    {noteLines.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setConfirmingOption(opt.id);
                    setDecision(opt.id);
                    setError(null);
                  }}
                  className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                  disabled={saving}
                >
                  Accept
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={decline}
          className="px-4 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-60"
          disabled={saving}
        >
          Decline / no decision
        </button>
      </div>

      {confirmingOption ? (
        <div ref={confirmRef} className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Confirm selection</h2>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 space-y-3">
            <div className="text-sm text-amber-900 font-semibold">Authorization to proceed (required)</div>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={authorizationToProceed}
                onChange={(e) => setAuthorizationToProceed(e.target.checked)}
                className="w-5 h-5"
              />
              <span className="text-sm text-amber-900 font-medium">I authorize the selected option</span>
            </label>
          </div>

          <div className="bg-gray-50 rounded-2xl p-4">
            <div className="text-sm font-semibold text-gray-900">Customer name (required)</div>
            <input
              value={signedName}
              onChange={(e) => setSignedName(e.target.value)}
              className="mt-2 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white"
              placeholder="Type customer name"
            />
          </div>

          {savedSignatureDataUrl ? (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
              <div className="text-sm font-semibold text-green-900">Signature saved</div>
              <div className="text-sm text-green-800 mt-1">{savedSignedAt ? `Saved ${savedSignedAt}` : 'Saved previously'}</div>
              <div className="mt-3 rounded-xl bg-white border border-green-200 p-3">
                <img src={savedSignatureDataUrl} alt="Saved signature" className="max-h-32" />
              </div>
              <div className="text-xs text-green-800 mt-2">Signing below will replace the saved signature.</div>
            </div>
          ) : null}

          <SignaturePad heightPx={220} onChange={setSignatureDraft} />

          {signatureDraft.isEmpty && !savedSignatureDataUrl ? (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              Signature is required to confirm.
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setConfirmingOption(null)}
              className="px-4 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-60"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmAccept}
              className="px-4 py-2 rounded-xl bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
              disabled={saving}
            >
              Confirm & Save
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
